import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';
import { TrendingUp, TrendingDown, Plus, Edit3, Save, Trash2, RefreshCw, Lock, Minus, StickyNote } from 'lucide-react';
import { ResponsiveContainer, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { cn } from './ui/Table';
import { DataGrid, GridCol } from './ui/DataGrid';
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { Input } from './ui/arc/input';
import { Textarea } from './ui/arc/textarea';
import { EmptyState } from './ui/arc/empty-state';
import SegmentedControl from './ui/arc/segmented-control';
import { Dialog, DialogContent } from './ui/arc/dialog';
import { PageHeader, StatCard, Panel, ScreenSkeleton } from './costs/plata-kit';

type InvestmentType =
    | 'accion' | 'cedear' | 'cripto' | 'plazo_fijo' | 'fondo_comun'
    | 'bono' | 'on' | 'etf' | 'dolar_mep' | 'dolar_blue' | 'propiedad' | 'otro';

const TYPE_LABELS: Record<InvestmentType, string> = {
    accion: 'Acción',
    cedear: 'CEDEAR',
    cripto: 'Cripto',
    plazo_fijo: 'Plazo fijo',
    fondo_comun: 'Fondo común',
    bono: 'Bono',
    on: 'Obligación negociable',
    etf: 'ETF',
    dolar_mep: 'Dólar MEP',
    dolar_blue: 'Dólar blue',
    propiedad: 'Propiedad',
    otro: 'Otro',
};

const TYPE_COLORS: Record<InvestmentType, string> = {
    accion: '#3b82f6',
    cedear: '#f97316',
    cripto: '#f59e0b',
    plazo_fijo: '#10b981',
    fondo_comun: '#8b5cf6',
    bono: '#06b6d4',
    on: '#0891b2',
    etf: '#ef4444',
    dolar_mep: '#22c55e',
    dolar_blue: '#a855f7',
    propiedad: '#0ea5e9',
    otro: '#6b7280',
};

// Tipos que se compran por unidades — habilita campos de cantidad/precio unitario.
const UNIT_BASED_TYPES: InvestmentType[] = ['accion', 'cedear', 'cripto', 'etf', 'bono', 'on', 'fondo_comun'];

interface Investment {
    id: number;
    name: string;
    platform: string | null;
    type: InvestmentType;
    currency: 'ARS' | 'USD';
    amount_invested: number;
    current_value: number | null;
    current_value_updated_at: string | null;
    opened_at: string;
    closed_at: string | null;
    status: 'open' | 'closed';
    notes: string | null;
    quantity: number | null;
    unit_cost: number | null;
    unit_current: number | null;
    fees: number | null;
    broker_account: string | null;
    created_at: string;
    updated_at: string;
}

interface Valuation {
    id: number;
    investment_id: number;
    value: number;
    valued_at: string;
    note: string | null;
}

interface Props {
    userEmail: string | undefined;
}

const ALLOWED_EMAILS = ['martin@krilic.com.ar', 'stephanie@krilic.com.ar'];

const fmtMoney = (n: number, currency: 'ARS' | 'USD' = 'ARS') =>
    `${currency === 'USD' ? 'USD ' : '$'}${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;

function daysBetween(a: string | Date, b: string | Date = new Date()): number {
    const d1 = new Date(a).getTime();
    const d2 = new Date(b).getTime();
    return Math.max(0, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
}

export default function Investments({ userEmail }: Props) {
    const [items, setItems] = useState<Investment[]>([]);
    const [valuations, setValuations] = useState<Valuation[]>([]);
    const [usdRate, setUsdRate] = useState<number>(1);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<Investment | null>(null);
    const [showNew, setShowNew] = useState(false);
    const [updatingValueFor, setUpdatingValueFor] = useState<Investment | null>(null);
    const [filterStatus, setFilterStatus] = useState<'open' | 'closed' | 'all'>('open');
    const [filterType, setFilterType] = useState<string>('all');
    const [filterPlatform, setFilterPlatform] = useState<string>('all');
    const [search, setSearch] = useState('');

    const allowed = !!userEmail && ALLOWED_EMAILS.includes(userEmail);

    async function loadAll() {
        setLoading(true);
        try {
            const [invRes, valRes, settingsRes] = await Promise.all([
                supabase.from('investments').select('*').order('opened_at', { ascending: false }),
                supabase.from('investment_valuations').select('*').order('valued_at', { ascending: true }),
                supabase.from('cost_settings').select('usd_rate').eq('id', 1).maybeSingle(),
            ]);
            if (invRes.error) throw invRes.error;
            setItems((invRes.data || []) as Investment[]);
            setValuations((valRes.data || []) as Valuation[]);
            setUsdRate(Number(settingsRes.data?.usd_rate) || 1);
        } catch (e: any) {
            console.error(e);
            alert('Error cargando inversiones: ' + (e?.message || e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (allowed) loadAll();
    }, [allowed]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter(i => {
            if (filterStatus !== 'all' && i.status !== filterStatus) return false;
            if (filterType !== 'all' && i.type !== filterType) return false;
            if (filterPlatform !== 'all' && (i.platform || '—') !== filterPlatform) return false;
            if (q && !i.name.toLowerCase().includes(q) && !(i.platform || '').toLowerCase().includes(q)) return false;
            return true;
        });
    }, [items, filterStatus, filterType, filterPlatform, search]);

    const platforms = useMemo(() => {
        const set = new Set<string>();
        items.forEach(i => set.add(i.platform || '—'));
        return Array.from(set).sort();
    }, [items]);

    // Stats por moneda (sobre lo filtrado, abiertas para "actual" y todas para "histórico")
    const stats = useMemo(() => {
        const openItems = items.filter(i => i.status === 'open');
        const byCurrency = (curr: 'ARS' | 'USD') => {
            const subset = openItems.filter(i => i.currency === curr);
            const invertido = subset.reduce((s, i) => s + Number(i.amount_invested || 0), 0);
            const actual = subset.reduce((s, i) => s + Number(i.current_value ?? i.amount_invested), 0);
            const ganancia = actual - invertido;
            const pct = invertido > 0 ? (ganancia / invertido) * 100 : 0;
            return { invertido, actual, ganancia, pct, count: subset.length };
        };
        const ars = byCurrency('ARS');
        const usd = byCurrency('USD');
        const consolidadoARS = {
            invertido: ars.invertido + usd.invertido * usdRate,
            actual: ars.actual + usd.actual * usdRate,
        };
        const consolidadoGan = consolidadoARS.actual - consolidadoARS.invertido;
        const consolidadoPct = consolidadoARS.invertido > 0 ? (consolidadoGan / consolidadoARS.invertido) * 100 : 0;

        // Cerradas: ganancia realizada
        const closedItems = items.filter(i => i.status === 'closed');
        const realizadaARS = closedItems
            .filter(i => i.currency === 'ARS')
            .reduce((s, i) => s + (Number(i.current_value ?? i.amount_invested) - Number(i.amount_invested)), 0);
        const realizadaUSD = closedItems
            .filter(i => i.currency === 'USD')
            .reduce((s, i) => s + (Number(i.current_value ?? i.amount_invested) - Number(i.amount_invested)), 0);

        return { ars, usd, consolidadoARS, consolidadoGan, consolidadoPct, realizadaARS, realizadaUSD };
    }, [items, usdRate]);

    // Distribución por tipo (en ARS consolidado)
    const distByType = useMemo(() => {
        const map = new Map<string, number>();
        items.filter(i => i.status === 'open').forEach(i => {
            const value = Number(i.current_value ?? i.amount_invested);
            const arsValue = i.currency === 'USD' ? value * usdRate : value;
            map.set(i.type, (map.get(i.type) || 0) + arsValue);
        });
        return Array.from(map.entries()).map(([type, value]) => ({
            name: TYPE_LABELS[type as InvestmentType] || type,
            value: Math.round(value),
            color: TYPE_COLORS[type as InvestmentType] || '#888',
        })).sort((a, b) => b.value - a.value);
    }, [items, usdRate]);

    const distByPlatform = useMemo(() => {
        const map = new Map<string, number>();
        items.filter(i => i.status === 'open').forEach(i => {
            const value = Number(i.current_value ?? i.amount_invested);
            const arsValue = i.currency === 'USD' ? value * usdRate : value;
            const k = i.platform || '—';
            map.set(k, (map.get(k) || 0) + arsValue);
        });
        const palette = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#22c55e', '#a855f7', '#0ea5e9', '#6b7280'];
        return Array.from(map.entries())
            .map(([name, value], i) => ({ name, value: Math.round(value), color: palette[i % palette.length] }))
            .sort((a, b) => b.value - a.value);
    }, [items, usdRate]);

    // Serie temporal del valor total de cartera (ARS consolidado)
    const evolution = useMemo(() => {
        // Por cada fecha en la que hay alguna valuación, calcular el valor total de cartera abierta
        const dates = new Set<string>();
        valuations.forEach(v => dates.add(new Date(v.valued_at).toISOString().slice(0, 10)));
        items.forEach(i => dates.add(new Date(i.opened_at).toISOString().slice(0, 10)));
        const sorted = Array.from(dates).sort();
        return sorted.map(d => {
            const dateMs = new Date(d + 'T23:59:59').getTime();
            let totalArs = 0;
            for (const i of items) {
                const openedMs = new Date(i.opened_at).getTime();
                if (openedMs > dateMs) continue;
                if (i.closed_at && new Date(i.closed_at).getTime() < new Date(d).getTime()) continue;
                // Última valuación <= d para esta inversión, o amount_invested si no hay
                const vs = valuations.filter(v => v.investment_id === i.id && new Date(v.valued_at).getTime() <= dateMs);
                const lastVal = vs.length ? vs[vs.length - 1].value : Number(i.amount_invested);
                totalArs += i.currency === 'USD' ? Number(lastVal) * usdRate : Number(lastVal);
            }
            return { date: d, total: Math.round(totalArs) };
        });
    }, [items, valuations, usdRate]);

    async function handleDelete(inv: Investment) {
        if (!window.confirm(`Eliminar "${inv.name}"? Esto borra también su historial de valuaciones.`)) return;
        const { error } = await supabase.from('investments').delete().eq('id', inv.id);
        if (error) { alert('Error: ' + error.message); return; }
        await loadAll();
    }

    // Cerrar una inversión: antes era un window.prompt; ahora un diálogo (misma escritura).
    const [closing, setClosing] = useState<Investment | null>(null);
    async function handleClose(inv: Investment, val: number) {
        const { error } = await supabase.from('investments').update({
            status: 'closed',
            closed_at: new Date().toISOString().slice(0, 10),
            current_value: val,
            current_value_updated_at: new Date().toISOString(),
        }).eq('id', inv.id);
        if (error) { alert('Error: ' + error.message); return; }
        await supabase.from('investment_valuations').insert({
            investment_id: inv.id, value: val, note: 'Cierre',
        });
        setClosing(null);
        await loadAll();
    }

    if (!allowed) {
        return (
            <div className="tw w-full min-w-0 pb-12 pt-1">
                <div className="rounded-[var(--radius-surface)] border border-line bg-card">
                    <EmptyState icon={<Lock size={20} />} title="Acceso restringido" description="Esta sección solo está disponible para administradores." />
                </div>
            </div>
        );
    }

    const ars0 = (n: number) => '$' + Math.round(n).toLocaleString('es-AR');
    const gainTone = (n: number) => n >= 0 ? 'text-[var(--success)]' : 'text-[var(--danger)]';
    const lastValued = (inv: Investment) => inv.current_value_updated_at ? daysBetween(inv.current_value_updated_at) : null;

    const cols: GridCol<Investment>[] = [
        {
            key: 'name', header: 'Inversión', sort: i => `${i.name} ${i.platform || ''}`, render: inv => (
                <div className="min-w-[180px]">
                    <div className="font-semibold text-ink" title={inv.notes || undefined}>{inv.name}{inv.notes && <StickyNote size={12} className="ml-1 inline text-faint" aria-label="Tiene notas" />}</div>
                    <div className="text-[length:var(--text-xs)] text-soft">
                        {inv.platform || 'sin plataforma'}
                        {inv.quantity != null && <> · {Number(inv.quantity).toLocaleString('es-AR')} unid{inv.unit_current != null && ` · ${fmtMoney(Number(inv.unit_current), inv.currency)} c/u`}</>}
                    </div>
                </div>
            ),
        },
        { key: 'type', header: 'Tipo', sort: i => TYPE_LABELS[i.type] || i.type, render: i => <Badge size="sm">{TYPE_LABELS[i.type] || i.type}</Badge>, hideOnMobile: true },
        { key: 'opened', header: 'Apertura', sort: i => i.opened_at, hideOnMobile: true, render: i => <span className="whitespace-nowrap">{new Date(i.opened_at).toLocaleDateString('es-AR')}<span className="ml-1 text-faint">· {daysBetween(i.opened_at, i.closed_at || new Date())} d</span></span> },
        { key: 'inv', header: 'Invertido', align: 'right', sort: i => Number(i.amount_invested), render: i => fmtMoney(Number(i.amount_invested), i.currency) },
        {
            key: 'cv', header: 'Valor actual', align: 'right', sort: i => Number(i.current_value ?? i.amount_invested), render: i => {
                const d = lastValued(i);
                return (
                    <div>
                        <div className="font-semibold text-ink">{fmtMoney(Number(i.current_value ?? i.amount_invested), i.currency)}</div>
                        {i.status === 'open' && <div className={cn('text-[length:var(--text-xs)]', d != null && d > 30 ? 'text-[var(--warning)]' : 'text-faint')}>{d == null ? 'sin valuar' : d === 0 ? 'valuada hoy' : `valuada hace ${d} d`}</div>}
                    </div>
                );
            },
        },
        {
            key: 'gan', header: 'Resultado', align: 'right', sort: i => Number(i.amount_invested) > 0 ? (Number(i.current_value ?? i.amount_invested) - Number(i.amount_invested)) / Number(i.amount_invested) : 0, render: i => {
                const gan = Number(i.current_value ?? i.amount_invested) - Number(i.amount_invested);
                const pct = i.amount_invested > 0 ? (gan / Number(i.amount_invested)) * 100 : 0;
                return (
                    <div className={cn('font-semibold', gainTone(gan))}>
                        <div>{gan >= 0 ? '+' : ''}{fmtMoney(gan, i.currency)}</div>
                        <div className="text-[length:var(--text-xs)]">{fmtPct(pct)}</div>
                    </div>
                );
            },
        },
        { key: 'status', header: 'Estado', sort: i => i.status, render: i => i.status === 'closed' ? <Badge size="sm">Cerrada</Badge> : <Badge size="sm" tone="success">Abierta</Badge> },
        {
            key: 'acc', header: '', align: 'right', render: inv => (
                <div className="flex justify-end gap-1">
                    {inv.status === 'open' && <Button variant="secondary" size="sm" onClick={() => setUpdatingValueFor(inv)} title="Cargar el valor de hoy"><RefreshCw size={14} /> Valuar</Button>}
                    <Button variant="ghost" size="sm" title="Editar" aria-label="Editar" onClick={() => setEditing(inv)}><Edit3 size={14} /></Button>
                    {inv.status === 'open' && <Button variant="ghost" size="sm" title="Cerrar inversión" aria-label="Cerrar inversión" onClick={() => setClosing(inv)}><Minus size={14} /></Button>}
                    <Button variant="ghost" size="sm" title="Eliminar" aria-label="Eliminar" onClick={() => handleDelete(inv)}><Trash2 size={14} /></Button>
                </div>
            ),
        },
    ];

    const filtersActive = filterStatus !== 'open' || filterType !== 'all' || filterPlatform !== 'all';
    const realizada = stats.realizadaARS + stats.realizadaUSD * usdRate;
    const openCount = stats.ars.count + stats.usd.count;

    return (
        <div className="tw w-full min-w-0 pb-12 pt-1">
            <PageHeader title="Inversiones"
                description={<>Cartera abierta consolidada en pesos · dólar a ${usdRate.toLocaleString('es-AR')} (tipo de cambio de Config).</>}
                actions={<>
                    <Button variant="secondary" onClick={loadAll} aria-label="Actualizar"><RefreshCw size={16} /> Actualizar</Button>
                    <Button onClick={() => setShowNew(true)}><Plus size={16} /> Nueva inversión</Button>
                </>} />

            {loading && !items.length ? <ScreenSkeleton /> : <>
                {/* ── Resumen ─────────────────────────────────────────────────── */}
                <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <StatCard label="Invertido" value={ars0(stats.consolidadoARS.invertido)}
                        context={<>{openCount} abierta{openCount === 1 ? '' : 's'} · ARS {fmtMoney(stats.ars.invertido)}{stats.usd.count ? <> · {fmtMoney(stats.usd.invertido, 'USD')}</> : null}</>} />
                    <StatCard label="Valor actual" value={ars0(stats.consolidadoARS.actual)}
                        context={<>ARS {fmtMoney(stats.ars.actual)}{stats.usd.count ? <> · {fmtMoney(stats.usd.actual, 'USD')}</> : null}</>} />
                    <StatCard label="Resultado cartera abierta" tone={stats.consolidadoGan >= 0 ? 'success' : 'danger'}
                        value={`${stats.consolidadoGan >= 0 ? '+' : '−'}${ars0(Math.abs(stats.consolidadoGan))}`}
                        badge={<Badge size="sm" tone={stats.consolidadoGan >= 0 ? 'success' : 'danger'} icon={stats.consolidadoGan >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}>{fmtPct(stats.consolidadoPct)}</Badge>}
                        context="sobre lo invertido, sin realizar" />
                    <StatCard label="Ganancia realizada" value={ars0(realizada)} tone={realizada > 0 ? 'success' : realizada < 0 ? 'danger' : undefined}
                        context={<>de las cerradas · ARS {fmtMoney(stats.realizadaARS)}{stats.realizadaUSD ? <> · {fmtMoney(stats.realizadaUSD, 'USD')}</> : null}</>} />
                </section>

                {/* ── Gráficos: solo cuando dicen algo (una sola categoría = una barra al 100%, no aporta) ── */}
                {(distByType.length > 1 || distByPlatform.length > 1 || evolution.length >= 2) && (
                    <section className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-2">
                        {evolution.length >= 2 && (
                            <Panel title="Evolución del valor de la cartera" className="lg:col-span-2">
                                <EvolutionChart data={evolution} />
                            </Panel>
                        )}
                        {distByType.length > 1 && <Panel title="Por tipo"><ShareBars data={distByType} /></Panel>}
                        {distByPlatform.length > 1 && <Panel title="Por plataforma"><ShareBars data={distByPlatform} /></Panel>}
                    </section>
                )}

                {/* ── Tabla ───────────────────────────────────────────────────── */}
                <DataGrid rows={filtered} cols={cols} loading={loading} dense
                    searchPlaceholder="Buscar por nombre o plataforma…"
                    filtersActive={filtersActive}
                    onClearFilters={() => { setFilterStatus('open'); setFilterType('all'); setFilterPlatform('all'); }}
                    emptyTitle={items.length ? 'Sin inversiones abiertas' : 'Todavía no hay inversiones'}
                    emptyText={items.length ? 'Mirá las cerradas o cargá una nueva.' : 'Cargá la primera para seguir su valor en el tiempo.'}
                    emptyAction={<Button onClick={() => setShowNew(true)}><Plus size={16} /> Nueva inversión</Button>}
                    toolbar={<>
                        <SegmentedControl label="Estado" value={filterStatus} onValueChange={v => setFilterStatus(v as any)}
                            options={[
                                { value: 'open', label: 'Abiertas' },
                                { value: 'closed', label: 'Cerradas' },
                                { value: 'all', label: 'Todas' },
                            ]} />
                        <select value={filterType} onChange={e => setFilterType(e.target.value)} aria-label="Tipo" className={selectCls}>
                            <option value="all">Todos los tipos</option>
                            {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        {platforms.length > 1 && (
                            <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)} aria-label="Plataforma" className={selectCls}>
                                <option value="all">Todas las plataformas</option>
                                {platforms.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        )}
                    </>} />
            </>}

            {(showNew || editing) && (
                <InvestmentModal
                    initial={editing || undefined}
                    onClose={() => { setShowNew(false); setEditing(null); }}
                    onSaved={() => { setShowNew(false); setEditing(null); loadAll(); }}
                />
            )}
            {updatingValueFor && (
                <UpdateValueModal
                    inv={updatingValueFor}
                    onClose={() => setUpdatingValueFor(null)}
                    onSaved={() => { setUpdatingValueFor(null); loadAll(); }}
                />
            )}
            {closing && <CloseModal inv={closing} onClose={() => setClosing(null)} onConfirm={v => handleClose(closing, v)} />}
        </div>
    );
}

const selectCls = 'h-[var(--control-height-sm)] rounded-[var(--radius-control)] border border-line bg-card px-2 text-[length:var(--text-sm)] text-ink';

// Color de serie única (paleta de referencia de la skill dataviz, slot 1). Una sola serie:
// el título nombra lo que se ve, sin leyenda; textos en tinta, nunca del color de la serie.
const SERIES_1 = '#2a78d6';

/** Composición de la cartera: barras horizontales con etiqueta y % (más legible que una torta). */
function ShareBars({ data }: { data: { name: string; value: number }[] }) {
    const total = data.reduce((s, d) => s + d.value, 0) || 1;
    return (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
            {data.map(d => {
                const pct = (d.value / total) * 100;
                return (
                    <li key={d.name} title={`${d.name}: $${d.value.toLocaleString('es-AR')} (${pct.toFixed(1)}%)`}>
                        <div className="mb-1 flex justify-between gap-2 text-[length:var(--text-sm)]">
                            <span className="truncate text-ink">{d.name}</span>
                            <span className="shrink-0 tabular-nums text-soft">${d.value.toLocaleString('es-AR')} · {pct.toFixed(1).replace('.', ',')}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-muted">
                            <div className="h-2 rounded-full" style={{ width: `${Math.max(pct, 1)}%`, background: SERIES_1 }} />
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

function EvolutionChart({ data }: { data: { date: string; total: number }[] }) {
    const fmtD = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
    return (
        <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke="var(--border)" />
                <XAxis dataKey="date" tickFormatter={fmtD} tick={{ fontSize: 12, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tickFormatter={v => `$${(v / 1_000_000).toLocaleString('es-AR', { maximumFractionDigits: 1 })}M`} tick={{ fontSize: 12, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} width={56} />
                <Tooltip cursor={{ stroke: 'var(--border-strong)' }} labelFormatter={(d: any) => fmtD(String(d))}
                    formatter={(v: any) => [`$${Number(v).toLocaleString('es-AR')}`, 'Valor de la cartera']}
                    contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }} />
                <Line type="monotone" dataKey="total" stroke={SERIES_1} strokeWidth={2} dot={false} activeDot={{ r: 4, stroke: 'var(--card)', strokeWidth: 2 }} />
            </LineChart>
        </ResponsiveContainer>
    );
}

/** Cierre de una inversión: pide el valor final (antes era un window.prompt). */
function CloseModal({ inv, onClose, onConfirm }: { inv: Investment; onClose: () => void; onConfirm: (v: number) => Promise<void> }) {
    const [value, setValue] = useState(String(inv.current_value ?? inv.amount_invested));
    const [saving, setSaving] = useState(false);
    const v = Number(value);
    const ok = value.trim() !== '' && Number.isFinite(v);
    const gan = v - Number(inv.amount_invested);
    return (
        <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
            <DialogContent title={`Cerrar ${inv.name}`} description="Queda como ganancia realizada con el valor final que cargues.">
                <div className="flex flex-col gap-3">
                    <Input label={`Valor final (${inv.currency})`} type="number" step="0.01" value={value} onChange={e => setValue(e.target.value)} autoFocus
                        error={value.trim() !== '' && !ok ? 'Valor inválido' : undefined} />
                    {ok && <div className="text-[length:var(--text-sm)] text-soft">Invertido {fmtMoney(Number(inv.amount_invested), inv.currency)} · resultado <b className={gan >= 0 ? 'text-[var(--success)]' : 'text-[var(--danger)]'}>{gan >= 0 ? '+' : ''}{fmtMoney(gan, inv.currency)}</b></div>}
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
                        <Button variant="danger" disabled={!ok} loading={saving} onClick={async () => { setSaving(true); await onConfirm(v); setSaving(false); }}>Cerrar inversión</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

interface InvestmentModalProps {
    initial?: Investment;
    onClose: () => void;
    onSaved: () => void;
}

function InvestmentModal({ initial, onClose, onSaved }: InvestmentModalProps) {
    const isNew = !initial;
    const [name, setName] = useState(initial?.name || '');
    const [platform, setPlatform] = useState(initial?.platform || '');
    const [brokerAccount, setBrokerAccount] = useState(initial?.broker_account || '');
    const [type, setType] = useState<InvestmentType>(initial?.type || 'accion');
    const [currency, setCurrency] = useState<'ARS' | 'USD'>(initial?.currency || 'ARS');
    const [quantity, setQuantity] = useState<string>(initial?.quantity != null ? String(initial.quantity) : '');
    const [unitCost, setUnitCost] = useState<string>(initial?.unit_cost != null ? String(initial.unit_cost) : '');
    const [unitCurrent, setUnitCurrent] = useState<string>(initial?.unit_current != null ? String(initial.unit_current) : '');
    const [fees, setFees] = useState<string>(initial?.fees != null ? String(initial.fees) : '');
    const [amountInvested, setAmountInvested] = useState<string>(String(initial?.amount_invested ?? ''));
    const [currentValue, setCurrentValue] = useState<string>(initial?.current_value != null ? String(initial.current_value) : '');
    const [openedAt, setOpenedAt] = useState<string>(initial?.opened_at || new Date().toISOString().slice(0, 10));
    const [notes, setNotes] = useState(initial?.notes || '');
    const [saving, setSaving] = useState(false);

    const isUnitBased = UNIT_BASED_TYPES.includes(type);

    // Auto-calcular monto invertido = quantity × unit_cost + fees cuando todos están seteados
    // (solo si no fue tocado manualmente — heurística simple: si el monto está vacío o coincide con un cálculo previo)
    useEffect(() => {
        if (!isUnitBased) return;
        const q = Number(quantity);
        const uc = Number(unitCost);
        const f = Number(fees) || 0;
        if (!Number.isFinite(q) || !Number.isFinite(uc) || q <= 0 || uc <= 0) return;
        const calculated = (q * uc) + f;
        // Solo auto-rellenar si el campo está vacío
        setAmountInvested(prev => (prev === '' || prev === '0') ? calculated.toFixed(2) : prev);
    }, [quantity, unitCost, fees, isUnitBased]);

    // Auto valor actual = quantity × unit_current
    useEffect(() => {
        if (!isUnitBased) return;
        const q = Number(quantity);
        const ucur = Number(unitCurrent);
        if (!Number.isFinite(q) || !Number.isFinite(ucur) || q <= 0 || ucur <= 0) return;
        setCurrentValue((q * ucur).toFixed(2));
    }, [quantity, unitCurrent, isUnitBased]);

    async function handleSave() {
        if (!name.trim()) { alert('Faltó el nombre'); return; }
        const inv = Number(amountInvested);
        if (!Number.isFinite(inv) || inv < 0) { alert('Monto inicial inválido'); return; }
        const cv = currentValue.trim() === '' ? null : Number(currentValue);
        if (cv != null && !Number.isFinite(cv)) { alert('Valor actual inválido'); return; }
        const q = quantity.trim() === '' ? null : Number(quantity);
        const uc = unitCost.trim() === '' ? null : Number(unitCost);
        const ucur = unitCurrent.trim() === '' ? null : Number(unitCurrent);
        const f = fees.trim() === '' ? null : Number(fees);

        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                platform: platform.trim() || null,
                broker_account: brokerAccount.trim() || null,
                type,
                currency,
                amount_invested: inv,
                current_value: cv,
                current_value_updated_at: cv != null ? new Date().toISOString() : null,
                opened_at: openedAt,
                notes: notes.trim() || null,
                quantity: q,
                unit_cost: uc,
                unit_current: ucur,
                fees: f,
            };
            if (isNew) {
                const { data, error } = await supabase.from('investments').insert(payload).select('id').single();
                if (error) throw error;
                if (cv != null) {
                    await supabase.from('investment_valuations').insert({
                        investment_id: data.id, value: cv, note: 'Carga inicial',
                    });
                }
            } else {
                const { error } = await supabase.from('investments').update(payload).eq('id', initial!.id);
                if (error) throw error;
                if (cv != null && Number(initial!.current_value) !== cv) {
                    await supabase.from('investment_valuations').insert({
                        investment_id: initial!.id, value: cv, note: 'Actualización manual',
                    });
                }
            }
            onSaved();
        } catch (e: any) {
            alert('Error: ' + (e?.message || e));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
            <DialogContent title={isNew ? 'Nueva inversión' : 'Editar inversión'} className="!w-[min(680px,calc(100vw-32px))]">
                <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Input label="Nombre / Especie" value={name} onChange={e => setName(e.target.value)} placeholder="Ej: AMZN CEDEAR, BTC, PF Galicia 30d" autoFocus />
                        <Input label="Plataforma / Broker" value={platform} onChange={e => setPlatform(e.target.value)} placeholder="IOL, Binance, Galicia, BBVA…" />
                        <label className="flex flex-col gap-1.5 text-[length:var(--text-sm)] font-medium text-ink">
                            Tipo
                            <select value={type} onChange={e => setType(e.target.value as InvestmentType)} className={cn(selectCls, 'h-[var(--control-height-md)] w-full font-normal')}>
                                {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                            </select>
                        </label>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-[length:var(--text-sm)] font-medium text-ink">Moneda</span>
                            <SegmentedControl label="Moneda" value={currency} onValueChange={v => setCurrency(v as 'ARS' | 'USD')}
                                options={[{ value: 'ARS', label: 'Pesos (ARS)' }, { value: 'USD', label: 'Dólares (USD)' }]} />
                        </div>
                        <Input label="Cuenta inversora (opcional)" value={brokerAccount} onChange={e => setBrokerAccount(e.target.value)} placeholder="Nº de cuenta del broker" />
                        <Input label="Fecha de apertura" type="date" value={openedAt} onChange={e => setOpenedAt(e.target.value)} />
                    </div>

                    {isUnitBased && (
                        <div>
                            <div className="mb-2 text-[length:var(--text-xs)] font-semibold uppercase tracking-wide text-faint">Compra por unidades · calcula el monto invertido</div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <Input label="Cantidad / Nominales" type="number" step="0.000001" value={quantity} onChange={e => setQuantity(e.target.value)} placeholder="859" />
                                <Input label="Precio compra (por unidad)" type="number" step="0.0001" value={unitCost} onChange={e => setUnitCost(e.target.value)} placeholder="2662.50" />
                                <Input label="Comisiones + IVA" type="number" step="0.01" value={fees} onChange={e => setFees(e.target.value)} placeholder="32378.31" />
                                <Input label="Precio actual (por unidad)" type="number" step="0.0001" value={unitCurrent} onChange={e => setUnitCurrent(e.target.value)} placeholder="Opcional" />
                            </div>
                        </div>
                    )}

                    <div>
                        <div className="mb-2 text-[length:var(--text-xs)] font-semibold uppercase tracking-wide text-faint">Totales</div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Input label="Monto invertido (con comisiones)" type="number" step="0.01" value={amountInvested} onChange={e => setAmountInvested(e.target.value)} />
                            <Input label={`Valor actual total (${currency})`} type="number" step="0.01" value={currentValue} onChange={e => setCurrentValue(e.target.value)} placeholder="Opcional al crear" />
                        </div>
                    </div>

                    <Textarea label="Notas" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Detalle, tasa, vencimiento, link al broker, etc." />

                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
                        <Button onClick={handleSave} loading={saving}><Save size={16} /> {saving ? 'Guardando…' : 'Guardar'}</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

interface UpdateValueModalProps {
    inv: Investment;
    onClose: () => void;
    onSaved: () => void;
}

function UpdateValueModal({ inv, onClose, onSaved }: UpdateValueModalProps) {
    const hasUnits = inv.quantity != null && Number(inv.quantity) > 0;
    const [unitPrice, setUnitPrice] = useState<string>(
        inv.unit_current != null ? String(inv.unit_current) : ''
    );
    const [value, setValue] = useState<string>(String(inv.current_value ?? inv.amount_invested));
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    // Si tiene unidades, actualizar value cuando cambia unitPrice
    useEffect(() => {
        if (!hasUnits) return;
        const up = Number(unitPrice);
        if (!Number.isFinite(up) || up <= 0) return;
        setValue((up * Number(inv.quantity)).toFixed(2));
    }, [unitPrice, hasUnits, inv.quantity]);

    async function handleSave() {
        const v = Number(value);
        if (!Number.isFinite(v)) { alert('Valor inválido'); return; }
        const up = unitPrice.trim() === '' ? null : Number(unitPrice);
        setSaving(true);
        try {
            const updatePayload: any = {
                current_value: v,
                current_value_updated_at: new Date().toISOString(),
            };
            if (hasUnits && up != null && Number.isFinite(up)) {
                updatePayload.unit_current = up;
            }
            const { error: e1 } = await supabase.from('investments').update(updatePayload).eq('id', inv.id);
            if (e1) throw e1;
            await supabase.from('investment_valuations').insert({
                investment_id: inv.id,
                value: v,
                note: note.trim() || null,
            });
            onSaved();
        } catch (e: any) {
            alert('Error: ' + (e?.message || e));
        } finally {
            setSaving(false);
        }
    }

    const cv = Number(value);
    const gan = cv - Number(inv.amount_invested);
    const pct = inv.amount_invested > 0 ? (gan / Number(inv.amount_invested)) * 100 : 0;

    return (
        <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
            <DialogContent title={`Valuar ${inv.name}`} description="Se guarda en el historial y alimenta el gráfico de evolución.">
                <div className="flex flex-col gap-3">
                    {hasUnits && (
                        <Input label={`Precio actual por unidad (${inv.currency})`} description={`${Number(inv.quantity).toLocaleString('es-AR')} unidades · calcula el valor total`}
                            type="number" step="0.0001" value={unitPrice} onChange={e => setUnitPrice(e.target.value)} autoFocus />
                    )}
                    <Input label={`Valor actual total (${inv.currency})`} type="number" step="0.01" value={value} onChange={e => setValue(e.target.value)} autoFocus={!hasUnits} />
                    <div className="rounded-[var(--radius-control)] bg-muted px-3 py-2 text-[length:var(--text-sm)] text-soft">
                        Invertido <b className="text-ink">{fmtMoney(Number(inv.amount_invested), inv.currency)}</b> · resultado{' '}
                        <b className={gan >= 0 ? 'text-[var(--success)]' : 'text-[var(--danger)]'}>{gan >= 0 ? '+' : ''}{fmtMoney(gan, inv.currency)} ({fmtPct(pct)})</b>
                    </div>
                    <Input label="Nota (opcional)" value={note} onChange={e => setNote(e.target.value)} placeholder="Pago de dividendo, cobro de cupón, ajuste, etc." />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
                        <Button onClick={handleSave} loading={saving}><Save size={16} /> {saving ? 'Guardando…' : 'Guardar valuación'}</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
